"use client";

import { clearSignedOut, markSignedOut, SIGNED_OUT_KEY } from "@/lib/auth-logout";
import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useRef, useState } from "react";

function SignInContent() {
  const searchParams = useSearchParams();
  const [signedOut, setSignedOut] = useState<boolean | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const signInStarted = useRef(false);
  const error = searchParams.get("error");
  const sessionExpired = searchParams.get("sessionExpired") === "1";
  const callbackUrl = searchParams.get("callbackUrl") ?? "/";
  const autoSignIn = searchParams.get("auto") === "1";

  useEffect(() => {
    if (autoSignIn) {
      if (signInStarted.current) return;
      signInStarted.current = true;
      setSigningIn(true);
      clearSignedOut();
      void signIn("keycloak", { callbackUrl });
      return;
    }

    if (sessionExpired) {
      markSignedOut();
    }
    setSignedOut(
      sessionExpired ||
        searchParams.get("signedOut") === "1" ||
        sessionStorage.getItem(SIGNED_OUT_KEY) === "1"
    );
  }, [searchParams, sessionExpired, autoSignIn, callbackUrl]);

  if (autoSignIn) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        <h1 className="mt-6 text-lg font-semibold text-gray-900 dark:text-white">
          Signing you in…
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Taking you to the dashboard.
        </p>
      </div>
    );
  }

  // "Keep me signed in" lives on the Keycloak login form (realm rememberMe);
  // opted-in sessions get the 30-day remember-me idle timeout.
  function handleSignIn() {
    if (signingIn) return;
    setSigningIn(true);
    const forceLogin = signedOut || sessionExpired;
    clearSignedOut();
    if (forceLogin) {
      void signIn("keycloak", { callbackUrl }, { prompt: "login" });
      return;
    }
    void signIn("keycloak", { callbackUrl });
  }

  if (signedOut === null) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          Welcome back
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
          {sessionExpired
            ? "Your session has expired. Sign in to continue."
            : signedOut
              ? "You have been signed out. Sign in with the account you want to use — you'll be asked for credentials."
              : "Sign in to your dashboard to view calmness insights and reports."}
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {error === "Configuration"
            ? "Unable to connect to the authentication service. Please try again later."
            : `Authentication error: ${error}`}
        </div>
      )}

      <button
        type="button"
        disabled={signingIn}
        className="flex h-11 w-full items-center justify-center rounded-full text-sm font-bold btn-brand shadow-theme-sm disabled:cursor-not-allowed disabled:opacity-60"
        onClick={handleSignIn}
      >
        {signingIn ? "Signing in…" : "Sign in"}
      </button>

      <p className="text-center text-xs text-gray-500 dark:text-gray-400">
        New to CuesHub?{" "}
        <a
          href="https://cueshub.com"
          target="_blank"
          rel="noopener noreferrer"
          className="link-brand font-medium"
        >
          Learn more
        </a>
      </p>
    </div>
  );
}

export default function SignInPage() {
  return (
    <Suspense
      fallback={
        <div className="flex flex-col items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      }
    >
      <SignInContent />
    </Suspense>
  );
}
